/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer } from "react";
import { useToggle } from "react-use";
import styled from "styled-components";
import { Box, Button, FlexContainer, theme } from "@nordcloud/gnui";
import {
  ButtonGroup,
  DateRange,
  EstatePeriod,
  Granularity,
  GranularitySelector,
  PeriodSelector,
} from "~/components";
import { useQueryState } from "~/hooks";
import { ACTION, EstateGroupBy, GroupingSelector, reducer } from "./Charts";
import { FiltersSidebar } from "./FiltersSidebar";
import { FiltersProp } from "./types";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
  disableDailyView: boolean;
  activeChart: EstateGroupBy;
  onPeriodChange: (
    selectedPeriod: EstatePeriod,
    selectedRange?: DateRange
  ) => void;
  onGranularityChange: (granularity: Granularity) => void;
  clearRange: () => void;
  disabledFilters: boolean;
};

type QueryStateCharts = FiltersProp & {
  groupBy: string;
};

export function EstateFiltersAndChartParams({
  period,
  range,
  granularity,
  disableDailyView,
  activeChart,
  onPeriodChange,
  onGranularityChange,
  clearRange,
  disabledFilters,
}: Props) {
  const [isSidebarOpen, toggleIsSidebarOpen] = useToggle(false);

  const {
    state: { ...restFilters },
    updateQueryState,
  } = useQueryState<QueryStateCharts>();

  const [filters, updateFilters] = useReducer(reducer, {
    ...restFilters,
  });

  const handleUpdate = (newFilters: FiltersProp) => {
    updateFilters({ type: ACTION.UPDATE, payload: { filters: newFilters } });
  };

  const clearFilters = () => {
    updateFilters({ type: ACTION.CLEAR });
  };

  const toggleFilters = (openFilters: boolean) => {
    handleUpdate(restFilters);
    toggleIsSidebarOpen(openFilters);
  };

  return (
    <>
      <Box boxStyle="lightGrey" marginBottom={theme.spacing.spacing04}>
        <FlexContainer justifyContent="space-between">
          <FlexContainer alignItems="center" wrap="wrap">
            <PeriodSelector
              period={period}
              range={range}
              clearRange={clearRange}
              disabled={disabledFilters}
              onChange={onPeriodChange}
            />
            <GranularitySelector
              disableDailyView={disableDailyView}
              granularity={granularity}
              disableSelector={disabledFilters}
              onChange={onGranularityChange}
            />
            <GroupingSelector
              activeChart={activeChart}
              disabled={disabledFilters}
              handleGroupBySelect={(value) =>
                updateQueryState({
                  groupBy: value,
                })
              }
            />
          </FlexContainer>
          <StyledButtonGroup onClick={() => toggleFilters(true)}>
            <Button severity="low" icon="filter" disabled={disabledFilters}>
              Filter
            </Button>
            <ExpandButton
              severity="low"
              icon="caretDown"
              disabled={disabledFilters}
            />
          </StyledButtonGroup>
        </FlexContainer>
        <FiltersSidebar
          isSidebarOpen={isSidebarOpen}
          toggleIsSidebarOpen={toggleIsSidebarOpen}
          filters={filters}
          updateFilters={handleUpdate}
          toggleFilters={toggleFilters}
          clearFilters={clearFilters}
        />
      </Box>
    </>
  );
}

const ExpandButton = styled(Button)`
  border-top-right-radius: ${theme.radiusDefault};
  border-bottom-right-radius: ${theme.radiusDefault};
`;

const StyledButtonGroup = styled(ButtonGroup)`
  &:hover button {
    background-color: ${theme.color.interactive.secondaryHover};
  }
`;
